import "urlpattern-polyfill";
import { Lms } from "@verity/api-client/types";
import { PortalContext } from "../portal/portal-config";

const INTEGER_REGEX = /^\d+$/;

const COURSE_URL_PATTERNS: Record<Lms, URLPattern[]> = {
	canvas: [new URLPattern({ pathname: "/courses/:courseId{/*}?" })],
	moodle: [new URLPattern({ pathname: "/{*/}?course/view.php", search: "*id=:courseId*" })],
	BlackboardLearn: [
		new URLPattern({
			pathname: "/webapps/blackboard{/*}?",
			search: "*course_id=:courseId*",
		}),
		new URLPattern({ pathname: "/ultra/courses/:courseId/outline" }),
	],
	desire2learn: [new URLPattern({ pathname: "/d2l/home/:courseId{/*}?" })],
};

/**
 * Protocols and domains to scan for in a string to fuzzily determine if it is a URL
 */
const fuzzyProtocols = ["http"];
const fuzzyDomains = [".com", ".org", ".edu"];

/* Attempt to infer the course ID from passed in URL */
export function courseUrlToCourseId(lms: Lms, url: URL) {
	for (const pattern of COURSE_URL_PATTERNS[lms]) {
		try {
			const match = pattern.exec(url);
			if (match) {
				const courseId = match.pathname.groups.courseId ?? match.search.groups.courseId;
				if (courseId) {
					return courseId;
				}
			}
		} catch (e) {
			console.error(e);
		}
	}
	return null;
}

export function courseIdToCourseUrl(
	{ lms, portalHostname }: Pick<PortalContext, "lms" | "portalHostname">,
	courseId: string,
) {
	switch (lms) {
		case "canvas":
			return `https://${portalHostname}/courses/${courseId}`;
		case "moodle":
			return `https://${portalHostname}/course/view.php?id=${courseId}`;
		case "BlackboardLearn":
			// assume it can be accessed using /ultra, since there's no way to get the complete Blackboard Original course URL
			return `https://${portalHostname}/ultra/courses/${courseId}/outline`;
		case "desire2learn":
			return `https://${portalHostname}/d2l/home/${courseId}`;
	}
}

function isIntegerString(string: string) {
	const number = Number(string);
	return INTEGER_REGEX.test(string) && Number.isInteger(number) && number >= 0;
}

/**
 * Determines if the provided string is a valid course ID for a given LMS
 */
export function validateCourseId(lms: Lms, courseId: string) {
	switch (lms) {
		/* example: courses/123 */
		case "canvas": {
			return isIntegerString(courseId);
		}
		case "moodle": {
			return isIntegerString(courseId);
		}
		/* example: _123_456 */
		case "BlackboardLearn": {
			const regex = new RegExp("^_[1-9]+[0-9]*_[1-9]+[0-9]*$");
			return regex.test(courseId);
		}
		case "desire2learn": {
			return isIntegerString(courseId);
		}
	}

	return false;
}

export function determineCourseInputType(courseToAdd: string) {
	/* Attempt to determine if user wants to add via URL */
	const hasProtocol = fuzzyProtocols.some((protocol) => courseToAdd.startsWith(protocol));
	const hasTopLevelDomain = fuzzyDomains.some((domain) => courseToAdd.includes(domain));
	return hasProtocol || hasTopLevelDomain ? "url" : "id";
}
